import { Injectable, Injector } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { AuthService } from '../services/auth/auth.service'
import { BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError } from 'rxjs';
import { isTauri } from 'src/app/core/tauri/tauri-http';
import { DesktopTokenStore } from 'src/app/core/tauri/desktop-token-store';
import { permissionDenied$ } from 'src/app/services/permissions/permission-denied.bus';

@Injectable()

export class TokenInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private urlsToSkip: string[] = [
    'api.openai.com',
    'api.stripe.com',
    'dl.narratheque.io',
    // Funnel public des site versions (/scrap/{slug}) : un 401 y signifie
    // « code de vérification incorrect » — le laisser au composant, sinon un
    // simple mauvais code éjecte le visiteur (logout) de la page.
    '/api/site/',
    // Tunnel d'abonnement : authentifié par le setup_token (scope
    // subscription_setup) posé par l'appelant. Un compte léger qui convertit
    // son compte est connecté : y substituer son access_token donnait
    // « Forbidden — invalid scope ».
    '/auth/setup-subscription-checkout'
  ];

  // Endpoints d'auth publics : un 401 y signifie « identifiants invalides »,
  // pas une session expirée. Il ne faut ni tenter de refresh, ni déconnecter
  // (sinon un simple mauvais mot de passe éjecterait l'utilisateur, et un 401
  // sur la vérif 2FA fermerait la modale en pleine connexion). L'erreur est
  // laissée au composant appelant, qui affiche le message du body.
  private authEndpoints: string[] = [
    '/auth/login',
    '/auth/register',
    '/auth/register_sso',
    '/auth/register/verify',
    '/auth/register/resend',
    '/auth/activation',
    '/auth/2fa/verify',
    '/auth/2fa/resend',
    '/auth/refresh',
    '/users/reset-password',
    '/users/new-password',
    // Pages de partage publiques (/base/..., /share/...) : un 401 sur le
    // mot de passe de la page ne doit pas déconnecter un utilisateur
    // connecté qui visite le lien.
    '/public/shares/',
  ];

  constructor(public authService: AuthService, private desktopStore: DesktopTokenStore) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.shouldSkipUrl(request.url)) {
      return next.handle(request);
    }

    request = this.addToken(request, this.authService.getToken());

    return next.handle(request).pipe(
      catchError(error => {
        // Refus faute de droit (matrice des droits) : la matrice locale est
        // sans doute périmée → PermissionsService la recharge.
        if (error instanceof HttpErrorResponse && error.status === 403 && error.error?.permission) {
          permissionDenied$.next(error.error.permission);
        }
        if (error instanceof HttpErrorResponse && error.status === 401) {
          // Endpoints d'auth publics : pas de refresh ni de logout, le
          // composant appelant gère l'affichage de l'erreur.
          if (!this.isAuthEndpoint(request.url)) {
            if (error.error.error === 'Expired token') {
              return this.handle401Error(request, next);
            } else if (error.error.error !== 'Invalid credentials') {
              // Token invalide (pas juste expiré) -> déconnexion
              this.authService.logout();
            }
          }
        }
        return throwError(() => error);
      })
    );
  }

  private shouldSkipUrl(url: string): boolean {
    return this.urlsToSkip.some(skipUrl => url.includes(skipUrl));
  }

  private isAuthEndpoint(url: string): boolean {
    return this.authEndpoints.some(endpoint => url.includes(endpoint));
  }

  private addToken(request: HttpRequest<any>, token: any) {
    if (!token) return request;

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const currentDate = new Date().toISOString();

    const setHeaders: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      'X-User-Timezone': timezone,
      'X-User-Date': currentDate
    };

    // Signal au back qu'on est un client desktop : il renverra alors aussi le
    // refresh token dans le JSON (au lieu du seul cookie httpOnly) et
    // acceptera le refresh token dans le body sur /auth/refresh.
    if (isTauri()) {
      setHeaders['X-Client'] = 'desktop';
    }

    return request.clone({ setHeaders });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((tokenResponse: any) => {
          this.isRefreshing = false;
          const newToken = tokenResponse.access_token || tokenResponse.authorisation?.token;
          if (newToken) {
            localStorage.setItem('access_token', newToken);
          }
          // En desktop, on reçoit aussi un nouveau refresh token (rotation) :
          // on le persiste pour le prochain /auth/refresh.
          if (isTauri() && tokenResponse.refresh_token) {
            this.desktopStore.setRefreshToken(tokenResponse.refresh_token);
          }
          this.refreshTokenSubject.next(newToken);
          return next.handle(this.addToken(request, newToken));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.authService.logout();
          return throwError(() => err);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap((token) => next.handle(this.addToken(request, token)))
      );
    }
  }
}